import React, { useEffect, useState } from 'react';
import { Megaphone, AlertTriangle, AlertOctagon, Plus, Trash2, X, Calendar, Pencil } from 'lucide-react';
import { saListAnnouncements, saCreateAnnouncement, saUpdateAnnouncement, saDeleteAnnouncement } from '../../lib/db';
import { formatDate } from '../../lib/formatters';

const SEVERITIES = [
  { key: 'info',     label: 'Info',     Icon: Megaphone,     tone: 'text-indigo-300', dot: 'bg-indigo-500' },
  { key: 'warning',  label: 'Warning',  Icon: AlertTriangle, tone: 'text-amber-300',  dot: 'bg-amber-500'  },
  { key: 'critical', label: 'Critical', Icon: AlertOctagon,  tone: 'text-red-300',    dot: 'bg-red-500'    },
];

const AUDIENCES = [
  { key: 'all',       label: 'All firms' },
  { key: 'trial',     label: 'Trial firms' },
  { key: 'active',    label: 'Active firms' },
  { key: 'suspended', label: 'Suspended firms' },
];

const DISMISS_MODES = [
  { key: 'persistent', label: 'Persistent — user dismisses, never sees it again' },
  { key: 'session',    label: 'Per-session — reappears on next page load' },
  { key: 'none',       label: 'Not dismissable — stays until it expires' },
];

const SEVERITY_DEFAULT_DISMISS = {
  info:     'persistent',
  warning:  'session',
  critical: 'none',
};

function severityMeta(key) {
  return SEVERITIES.find((s) => s.key === key) || SEVERITIES[0];
}

function audienceLabel(key) {
  return AUDIENCES.find((a) => a.key === key)?.label || (key === 'firm' ? 'Single firm' : key);
}

function dismissModeLabel(key) {
  switch (key) {
    case 'persistent': return 'Persistent';
    case 'session':    return 'Per-session';
    case 'none':       return 'Not dismissable';
    default:           return key || '—';
  }
}

// Convert an ISO timestamp from the DB into the value the
// <input type="datetime-local"> expects (local time, no seconds, no Z).
// Falls back to '' so the input renders empty for null/missing values.
function toDatetimeLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Dual-mode modal: when `editing` is a row, the form prefills with its
// values and submitting calls saUpdateAnnouncement. Otherwise it acts as
// the original "new announcement" flow.
function ComposeModal({ onClose, onSaved, editing }) {
  const isEdit = !!editing;

  const [title,    setTitle]    = useState(editing?.title    || '');
  const [body,     setBody]     = useState(editing?.body     || '');
  const [severity, setSeverity] = useState(editing?.severity || 'info');
  const [audience, setAudience] = useState(editing?.audience || 'all');
  const [expires,  setExpires]  = useState(toDatetimeLocalInput(editing?.expires_at));
  // null means "follow the severity default". For edit mode we start
  // touched=true with the row's existing dismiss_mode so the user sees
  // the actual current setting; for create mode we start touched=false
  // so the dropdown snaps to the severity-default as they pick severity.
  const [dismissMode,         setDismissMode]         = useState(editing?.dismiss_mode || null);
  const [dismissModeTouched,  setDismissModeTouched]  = useState(isEdit);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const effectiveDismiss = dismissModeTouched
    ? dismissMode
    : SEVERITY_DEFAULT_DISMISS[severity];

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError('');

    if (isEdit) {
      // Edit path. Build a payload of just-the-changed-fields. For
      // expires_at specifically: if the input is empty AND the row
      // currently has a value, ask the RPC to CLEAR the field. The RPC
      // distinguishes "send null = keep current" from "list in clear =
      // explicitly null" so we can revert "expires on date" back to
      // "never expires" in one click.
      const clear = [];
      const payload = {
        title:        title.trim(),
        body:         body.trim() || null,
        severity,
        audience,
        dismiss_mode: dismissModeTouched ? dismissMode : null,
      };
      if (expires) {
        payload.expires_at = new Date(expires).toISOString();
      } else if (editing.expires_at) {
        clear.push('expires_at');
      }
      // body is special — empty string should clear it (not "keep current").
      if (!body.trim() && editing.body) clear.push('body');

      const { error: err } = await saUpdateAnnouncement(editing.id, payload, clear);
      setSaving(false);
      if (err) { setError(err.message || 'Could not save changes'); return; }
      onSaved();
      return;
    }

    // Create path (unchanged behavior from before).
    const { error: err } = await saCreateAnnouncement({
      title:      title.trim(),
      body:       body.trim() || null,
      severity,
      audience,
      expires_at: expires ? new Date(expires).toISOString() : null,
      dismiss_mode: dismissModeTouched ? dismissMode : null,
    });
    setSaving(false);
    if (err) { setError(err.message || 'Could not publish'); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">
            {isEdit ? 'Edit announcement' : 'New announcement'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs text-slate-400 mb-1">Title</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Scheduled maintenance window"
              className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Message (optional)</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="We'll be performing database maintenance on Sunday from 02:00–03:00 UTC."
              className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {SEVERITIES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Audience</label>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {AUDIENCES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Expires (optional)</label>
            <input
              type="datetime-local"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-slate-500 mt-1">Leave blank to keep the banner up until manually deleted.</p>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Dismissal behavior</label>
            <select
              value={effectiveDismiss}
              onChange={(e) => {
                setDismissMode(e.target.value);
                setDismissModeTouched(true);
              }}
              className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {DISMISS_MODES.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {dismissModeTouched
                ? <>Overriding the default for <span className="text-slate-300 capitalize">{severity}</span> announcements.</>
                : <>Default for <span className="text-slate-300 capitalize">{severity}</span>. Change severity above to update, or pick explicitly.</>}
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-xl text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            {saving
              ? (isEdit ? 'Saving…'   : 'Publishing…')
              : (isEdit ? 'Save changes' : 'Publish')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function SuperAdminAnnouncementsPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [showCompose, setShowCompose] = useState(false);
  // null = not editing. A row = edit-mode prefilled from that row.
  const [editingRow,  setEditingRow]  = useState(null);
  const [deletingId,  setDeletingId]  = useState(null);

  const refresh = () => {
    setLoading(true);
    saListAnnouncements().then(({ data, error: err }) => {
      if (err) setError(err.message || 'Could not load announcements');
      else setRows(data || []);
      setLoading(false);
    });
  };
  useEffect(refresh, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? Users who already dismissed it won't see it return.`)) return;
    setDeletingId(a.id);
    const { error: err } = await saDeleteAnnouncement(a.id);
    setDeletingId(null);
    if (err) { alert(err.message || 'Could not delete'); return; }
    refresh();
  };

  if (loading) return <div className="text-center py-20 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Announcements</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Broadcast banners that appear inside the tenant app. Users can dismiss individually.
          </p>
        </div>
        <button
          onClick={() => setShowCompose(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition"
        >
          <Plus size={14} /> New announcement
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl py-16 text-center">
          <Megaphone size={28} className="text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No announcements yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((a) => {
            const meta = severityMeta(a.severity);
            const Icon = meta.Icon;
            const expired = a.expires_at && new Date(a.expires_at) <= new Date();
            return (
              <div key={a.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold text-sm">{a.title}</h3>
                        <span className={`inline-flex items-center gap-1 text-[11px] ${meta.tone}`}>
                          <Icon size={11} /> {meta.label}
                        </span>
                        <span className="text-[11px] text-slate-500">·</span>
                        <span className="text-[11px] text-slate-400">{audienceLabel(a.audience)}</span>
                        {a.firm_name && <span className="text-[11px] text-slate-400">· {a.firm_name}</span>}
                        <span className="text-[11px] text-slate-500">·</span>
                        <span
                          className={`text-[11px] px-1.5 py-0.5 rounded ${
                            a.dismiss_mode === 'none'
                              ? 'bg-red-900/40 text-red-300'
                              : a.dismiss_mode === 'session'
                                ? 'bg-amber-900/30 text-amber-300'
                                : 'bg-slate-700 text-slate-300'
                          }`}
                          title={`Dismissal: ${dismissModeLabel(a.dismiss_mode)}`}
                        >
                          {dismissModeLabel(a.dismiss_mode)}
                        </span>
                        {expired && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">Expired</span>}
                      </div>
                      {a.body && <p className="text-sm text-slate-300 mt-1.5 whitespace-pre-wrap">{a.body}</p>}
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                        <span>Posted {formatDate(a.created_at, { includeTime: true })}</span>
                        {a.created_by_email && <span>by {a.created_by_email}</span>}
                        {a.expires_at && (
                          <span className="flex items-center gap-1">
                            <Calendar size={11} /> expires {formatDate(a.expires_at, { includeTime: true })}
                          </span>
                        )}
                        <span>· {a.dismissals || 0} dismissals</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setEditingRow(a)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs bg-slate-800 hover:bg-indigo-900/40 hover:text-indigo-300 text-slate-400 transition"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                    <button
                      onClick={() => remove(a)}
                      disabled={deletingId === a.id}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs bg-slate-800 hover:bg-red-900/40 hover:text-red-300 text-slate-400 transition disabled:opacity-40"
                    >
                      <Trash2 size={11} /> {deletingId === a.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Compose modal — opens for both create (showCompose=true,
          editingRow=null) and edit (editingRow=row). One modal, two
          modes, controlled by the `editing` prop. */}
      {(showCompose || editingRow) && (
        <ComposeModal
          editing={editingRow}
          onClose={() => { setShowCompose(false); setEditingRow(null); }}
          onSaved={() => {
            setShowCompose(false);
            setEditingRow(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
